import React, { useState, useEffect } from 'react';
import { FaTrash, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { useTranslation } from 'react-i18next';

const RegistrationHistory = ({ users, sessionRegistrationCounts, countries, cities, sessions, onSendGroupEmail }) => {
  const { i18n } = useTranslation();
  const [filterCountry, setFilterCountry] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSessionId, setExpandedSessionId] = useState(null); // New state for expanded session

  const [filteredUsers, setFilteredUsers] = useState([]);

  // Helper function to determine user status
  const getUserStatus = (user) => {
    if (user.isDeleted) {
      return <span className="status-badge status-deleted">Supprimé</span>;
    }

    // If sessions data is not yet loaded or user has no sessions, return unknown
    if (!sessions || sessions.length === 0 || !user.sessionIds || user.sessionIds.length === 0) {
      return <span className="status-badge status-unknown">Inconnu</span>;
    }

    const now = new Date();
    const userSessions = user.sessionIds.map(sId => sessions.find(s => s.id === sId)).filter(Boolean);

    // If after filtering, no valid sessions are found, it's unknown
    if (userSessions.length === 0) {
        return <span className="status-badge status-unknown">Inconnu</span>;
    }

    const hasOngoingSession = userSessions.some(session => {
        const sessionStartTime = new Date(session.startTime);
        const sessionEndTime = new Date(session.endTime);
        return sessionStartTime <= now && sessionEndTime > now;
    });

    const allSessionsCompleted = userSessions.every(session => {
        const sessionEndTime = new Date(session.endTime);
        return sessionEndTime <= now;
    });

    const allSessionsInFuture = userSessions.every(session => {
        const sessionStartTime = new Date(session.startTime);
        return sessionStartTime > now;
    });

    console.log("getUserStatus: hasOngoingSession:", hasOngoingSession); // NEW LOG
    console.log("getUserStatus: allSessionsCompleted:", allSessionsCompleted); // NEW LOG
    console.log("getUserStatus: allSessionsInFuture:", allSessionsInFuture); // NEW LOG

    if (hasOngoingSession) {
      return <span className="status-badge status-ongoing">En cours</span>;
    } else if (allSessionsCompleted) {
      return <span className="status-badge status-completed">Terminé</span>;
    } else if (allSessionsInFuture) {
      return <span className="status-badge status-upcoming">Non débuté</span>;
    } else if (userSessions.length > 0 && !hasOngoingSession && !allSessionsCompleted && !allSessionsInFuture) {
        // This case handles sessions that might be in a mixed state (some completed, some future, but none ongoing)
        // or if the logic above didn't catch a specific future scenario.
        // Given the user's request, if it's not ongoing or completed, and not explicitly in future (which it should be if all are future),
        // we can assume it's "Non débuté" if there are sessions.
        return <span className="status-badge status-upcoming">Non débuté</span>;
    }
    return <span className="status-badge status-unknown">Inconnu</span>; // Fallback for truly unknown or no sessions
  };

  useEffect(() => {
    let currentFilteredUsers = users;

    if (filterCountry) {
      currentFilteredUsers = currentFilteredUsers.filter(user =>
        user.countryName.toLowerCase().includes(filterCountry.toLowerCase())
      );
    }

    if (filterCity) {
      currentFilteredUsers = currentFilteredUsers.filter(user =>
        user.cityName.toLowerCase().includes(filterCity.toLowerCase())
      );
    }

    if (searchTerm) {
      currentFilteredUsers = currentFilteredUsers.filter(user =>
        user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // If a session is expanded, filter users for that session
    if (expandedSessionId) {
      currentFilteredUsers = currentFilteredUsers.filter(user =>
        user.sessionIds && user.sessionIds.includes(expandedSessionId)
      );
    }

    setFilteredUsers(currentFilteredUsers);
  }, [users, filterCountry, filterCity, searchTerm, expandedSessionId, sessions]);

  if (!users || users.length === 0) {
    return <p>Aucun historique d'inscription disponible pour le moment.</p>;
  }

  const handleSessionClick = (sessionId) => {
    setExpandedSessionId(prevId => (prevId === sessionId ? null : sessionId));
  };

  const handleSendEmailByCity = () => {
    const emails = filteredUsers.map(user => user.email);
    if (emails.length > 0) {
      onSendGroupEmail(emails);
    } else {
      alert("Aucun utilisateur filtré pour envoyer un e-mail.");
    }
  };

  return (
      <div className="table-container">
          <h3>Statistiques d'inscription par session</h3>
          <div className="stats-grid-wrapper">
            <div className="session-stats-summary">
            {Object.entries(sessionRegistrationCounts)
              .filter(([sessionId, sessionData]) => {
                const todayStart = new Date();
                todayStart.setHours(0, 0, 0, 0);
                return sessionData.startTime && new Date(sessionData.startTime) < todayStart;
              })
              .map(([sessionId, sessionData]) => {
              const city = cities.find(c => c.id === sessionData.cityId);
              const country = countries.find(co => co.id === city?.countryId);
              const cityName = city ? city.name[i18n.language.split('-')[0]] : 'N/A';
              const countryName = country ? country.name[i18n.language.split('-')[0]] : 'N/A';

              return (
                <div 
                  key={sessionId} 
                  className="stat-card session-card"
                  onClick={() => handleSessionClick(sessionId)}
                >
                  <h4>{sessionData.title}</h4>
                  <p>{sessionData.totalCount}</p>
                  <small>
  {sessionData.address}<br/> {/* Add address here */}
  {cityName}, {countryName} -
  {sessionData.startTime && sessionData.endTime ? (
    <div>
      {new Date(sessionData.startTime).toLocaleDateString(i18n.language)} {new Date(sessionData.startTime).toLocaleTimeString(i18n.language)} - {new Date(sessionData.endTime).toLocaleTimeString(i18n.language)}
    </div>
  ) : (
    'Pas de date disponible' // Fallback if no date
  )}
  <span style={{ color: 'green', fontWeight: 'bold', display: 'block', marginTop: '5px' }}>{sessionData.language}</span>
</small>
                  {expandedSessionId === sessionId ? <FaChevronUp /> : <FaChevronDown />}
                </div>
              );
            })}
          </div>
          </div>
          <div className="filter-bar">
              <span>Filtrer par:</span>
              <input
                  type="text"
                  placeholder="Pays"
                  value={filterCountry}
                  onChange={(e) => setFilterCountry(e.target.value)}
              />
              <input
                  type="text"
                  placeholder="Ville"
                  value={filterCity}
                  onChange={(e) => setFilterCity(e.target.value)}
              />
              <input
                  type="text"
                  placeholder="Rechercher par nom/prénom"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
              />
          </div>

          {expandedSessionId && (
            <div className="expanded-session-details">
              <h4>Utilisateurs inscrits pour la session: {sessionRegistrationCounts[expandedSessionId]?.title} ({sessionRegistrationCounts[expandedSessionId]?.address})</h4>
              <table>
                  <thead>
                      <tr>
                          <th>Prénom</th>
                          <th>Nom</th>
                          <th>Email</th>
                          <th>Pays</th>
                          <th>Ville</th>
                          <th>Téléphone</th>
                          <th>Date d'inscription</th>
                          <th>Session(s)</th>
                          <th>Statut</th>
                      </tr>
                  </thead>
                  <tbody>
                      {[...filteredUsers].sort((a, b) => new Date(b.registrationDate) - new Date(a.registrationDate)).map((user) => (
                          <tr key={user.id} className={user.isDeleted ? 'user-deleted' : ''}>
                              <td>{user.firstName}</td>
                              <td>{user.lastName}</td>
                              <td>{user.email}</td>
                              <td>{user.countryName}</td>
                              <td>{user.cityName}</td>
                              <td>{user.phoneNumber}</td>
                              <td>{new Date(user.registrationDate).toLocaleDateString()}</td>
                              <td>
                                {
                                  user.sessions && user.sessions.length > 0 ? (
                                    user.sessions.map((session, index) => (
                                      <div key={index} style={{ marginBottom: '5px' }}>
                                        <span>{session.title}</span> <span style={{ fontStyle: 'italic',  color: '#03c65bff', fontWeight:'bolder' }}>({session.language})</span>
                                        <small style={{ display: 'block', opacity: 0.7 }}>{session.address}</small>
                                      </div>
                                    ))
                                  ) : (
                                    <span>N/A</span>
                                  )
                                }
                              </td>
                              <td>{getUserStatus(user)}</td>
                          </tr>
                      ))}
                  </tbody>
              </table>
            </div>
          )}

          {!expandedSessionId && (
            <div className="all-users-table">
              <h4>Tous les utilisateurs inscrits</h4>
              <table>
                  <thead>
                      <tr>
                          <th>Prénom</th>
                          <th>Nom</th>
                          <th>Email</th>
                          <th>Pays</th>
                          <th>Ville</th>
                          <th>Téléphone</th>
                          <th>Date d'inscription</th>
                          <th>Session(s)</th>
                          <th>Statut</th>
                      </tr>
                  </thead>
                  <tbody>
                      {[...filteredUsers].sort((a, b) => new Date(b.registrationDate) - new Date(a.registrationDate)).map((user) => (
                          <tr key={user.id} className={user.isDeleted ? 'user-deleted' : ''}>
                              <td>{user.firstName}</td>
                              <td>{user.lastName}</td>
                              <td>{user.email}</td>
                              <td>{user.countryName}</td>
                              <td>{user.cityName}</td>
                              <td>{user.phoneNumber}</td>
                              <td>{new Date(user.registrationDate).toLocaleDateString()}</td>
                              <td>
                                {
                                  user.sessions && user.sessions.length > 0 ? (
                                    user.sessions.map((session, index) => (
                                      <div key={index} style={{ marginBottom: '5px' }}>
                                        <span>{session.title}</span> <span style={{ fontStyle: 'italic', color: '#03c65bff', fontWeight:'bolder' }}>({session.language})</span>
                                        <small style={{ display: 'block', opacity: 0.7 }}>{session.address}</small>
                                      </div>
                                    ))
                                  ) : (
                                    <span>N/A</span>
                                  )
                                }
                              </td>
                              <td>{getUserStatus(user)}</td>
                          </tr>
                      ))}
                  </tbody>
              </table>
            </div>
          )}
      </div>
  );
};

export default RegistrationHistory;